// Components/Custom/BenefitsModal.tsx
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch } from "Components/Hooks/hooks";
import Icon from "Components/Icons/Icon";
import React, { useState, useRef, DragEvent, ChangeEvent } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { addItemFromCSV } from "slices/thunks";

const api_url = process.env.REACT_APP_API_URL;
const secretToken = process.env.REACT_APP_SECRET_TOKEN;

interface BenefitsModalProps {
  isOpen: boolean;
  onCancel: () => void;
  currentPage?: number;
}

const ImportItemsModal: React.FC<BenefitsModalProps> = ({
  isOpen,
  onCancel,
  currentPage = 1,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch: any = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  // Validate file type
  const isValidFileType = (file: File): boolean => {
    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    return validTypes.includes(file.type) || file.name.endsWith(".csv");
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    setUploadError("");

    if (!isValidFileType(file)) {
      setUploadError("Please upload a valid CSV or XLSX file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      setUploadError("File size should not exceed 5MB");
      return;
    }

    setSelectedFile(file);
  };

  // Handle drag events
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle click on dropzone
  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file to upload");
      return;
    }

    try {
      console.log("Uploading file:", selectedFile);
      setIsLoading(true);

      await dispatch(
        addItemFromCSV({ file: selectedFile, existPage: currentPage }),
      )?.unwrap();
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setIsLoading(false);
      onCancel();
    }
  };

  // Handle download sample
  const handleDownloadSample = () => {
    const url = `${api_url}items/sample-template?secure_token=${secretToken}`;
    // Create a hidden anchor element
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "sample-file";

    // Append to DOM
    document.body.appendChild(link);

    // Trigger click
    link.click();

    // Remove element
    document.body.removeChild(link);
  };

  // Reset state when modal closes
  const handleCancel = () => {
    setSelectedFile(null);
    setUploadError("");
    setIsDragging(false);
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      size="md"
      backdrop="static"
    >
      <ModalHeader toggle={handleCancel} className="bg-greyF5 p-3 pe-4 ps-4">
        <span className="text-black2D fs-16 fw-medium font-primary">
          Import Items
        </span>
      </ModalHeader>

      <ModalBody className="pt-4 pb-4">
        <div className="import-wrapper">
          {/* Bullet points */}
          <ul className="import-info-list">
            <li>
              Download the sample CSV/XLSX file to see the required format.
            </li>
            <li>Required columns: Categories, Subcategories, Items</li>
          </ul>

          {/* Download sample */}
          <div
            className="download-link cursor-pointer"
            onClick={() => handleDownloadSample()}
          >
            <Icon name="PrimaryDownload" />
            <p className="text-primary fw-normal fs-14 font-primary text-decoration-underline p-0 m-0 ">
              Download Sample File
            </p>
          </div>

          {/* Drag & Drop */}
          <div
            className={`upload-dropzone ${isDragging ? "dragging" : ""} ${
              selectedFile ? "has-file" : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleDropzoneClick}
            style={{ cursor: "pointer" }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />

            {selectedFile ? (
              <div className="selected-file-info">
                <i className="ri-file-text-fill text-primary fs-3"></i>
                <p className="file-name text-black2D fw-medium fs-14 mb-1">
                  {selectedFile.name}
                </p>
                <p className="file-size text-grey89 fs-12">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </p>
                <button
                  className="btn btn-sm btn-link text-danger p-0 mt-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setUploadError("");
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <i className="ri-upload-cloud-2-fill upload-icon"></i>
                <p className="drop-text">
                  {isDragging
                    ? "Drop your file here..."
                    : "Drop files here or click to upload."}
                </p>
                <p className="file-format-text text-grey89 fs-12 mt-2">
                  Supported formats: CSV, XLSX (Max 5MB)
                </p>
              </>
            )}
          </div>

          {/* Error message */}
          {uploadError && (
            <div className="alert alert-danger mt-3 py-2 px-3 fs-14">
              {uploadError}
            </div>
          )}

          {/* Upload Button */}
          <CustomButton
            text="Upload"
            className="upload-btn bg-primary br-7 mt-3"
            onClick={handleUpload}
            loading={isLoading}
          />
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImportItemsModal;
